import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Deterministic string hash → uint32 (FNV-1a). Visual-only randomness. */
export function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/** Small seeded PRNG for procedural world building (never used by the sim). */
export function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const col = (hex: string | number): THREE.Color => new THREE.Color(hex);

/** Relative luminance of an sRGB colour string (0..1). */
export function luminance(hex: string): number {
  const c = new THREE.Color(hex); // linear components
  return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

export function colorDistance(a: string, b: string): number {
  const ca = new THREE.Color(a), cb = new THREE.Color(b);
  const ha = { h: 0, s: 0, l: 0 }, hb = { h: 0, s: 0, l: 0 };
  ca.getHSL(ha); cb.getHSL(hb);
  const dh = Math.min(Math.abs(ha.h - hb.h), 1 - Math.abs(ha.h - hb.h));
  return dh * Math.min(ha.s, hb.s) * 2 + Math.abs(ha.l - hb.l) + Math.abs(ha.s - hb.s) * 0.5;
}

export function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

export function easeOut(t: number): number { const c = Math.min(1, Math.max(0, t)); return 1 - (1 - c) * (1 - c); }

export function angleLerp(a: number, b: number, t: number): number {
  let d = b - a;
  while (d > Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

/**
 * Accumulates many small static pieces into one vertex-coloured, non-indexed
 * geometry so the whole static stadium is a single draw call.
 */
export class StaticBuilder {
  private parts: THREE.BufferGeometry[] = [];
  private tmpColor = new THREE.Color();

  add(geom: THREE.BufferGeometry, matrix: THREE.Matrix4 | null, color: THREE.ColorRepresentation): void {
    let g = geom.index ? geom.toNonIndexed() : geom.clone();
    for (const k of Object.keys(g.attributes)) if (k !== 'position' && k !== 'normal') g.deleteAttribute(k);
    if (!g.attributes.normal) g.computeVertexNormals();
    if (matrix) g.applyMatrix4(matrix);
    const n = g.attributes.position!.count;
    const c = this.tmpColor.set(color);
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { arr[i * 3] = c.r; arr[i * 3 + 1] = c.g; arr[i * 3 + 2] = c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(arr, 3));
    g.morphAttributes = {};
    this.parts.push(g);
  }

  box(w: number, h: number, d: number, x: number, y: number, z: number, color: THREE.ColorRepresentation, rotY = 0, rotX = 0, rotZ = 0): void {
    const g = new THREE.BoxGeometry(w, h, d);
    const m = new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rotX, rotY, rotZ, 'YXZ')),
      new THREE.Vector3(1, 1, 1));
    this.add(g, m, color);
    g.dispose();
  }

  cylinder(rTop: number, rBot: number, h: number, x: number, y: number, z: number, color: THREE.ColorRepresentation, segs = 8, rotX = 0, rotZ = 0): void {
    const g = new THREE.CylinderGeometry(rTop, rBot, h, segs, 1, false);
    const m = new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rotX, 0, rotZ)),
      new THREE.Vector3(1, 1, 1));
    this.add(g, m, color);
    g.dispose();
  }

  /** Raw triangle list (already world-space) with a flat colour. */
  tris(positions: number[], color: THREE.ColorRepresentation): void {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    g.computeVertexNormals();
    this.add(g, null, color);
    g.dispose();
  }

  get empty(): boolean { return this.parts.length === 0; }

  build(): THREE.BufferGeometry {
    const merged = mergeGeometries(this.parts, false);
    for (const p of this.parts) p.dispose();
    this.parts = [];
    if (!merged) throw new Error('StaticBuilder: merge failed');
    merged.computeBoundingSphere();
    return merged;
  }
}

/**
 * Collects raw coloured triangles/quads (with explicit normals) — used by the
 * swept stadium bowl where thousands of quads would be wasteful as geometries.
 */
export class TriSoup {
  pos: number[] = [];
  nor: number[] = [];
  colr: number[] = [];
  private a = new THREE.Vector3(); private b = new THREE.Vector3(); private n = new THREE.Vector3();

  /** Quad a-b-c-d (any winding). Normal oriented toward `facing` (approx desired normal). */
  quad(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3, color: THREE.Color, facing: THREE.Vector3): void {
    this.a.subVectors(b, a); this.b.subVectors(c, a);
    this.n.crossVectors(this.a, this.b);
    if (this.n.lengthSq() < 1e-12) { this.a.subVectors(c, a); this.b.subVectors(d, a); this.n.crossVectors(this.a, this.b); }
    this.n.normalize();
    let flip = this.n.dot(facing) < 0;
    if (flip) this.n.negate();
    const order = flip ? [a, c, b, a, d, c] : [a, b, c, a, c, d];
    for (const v of order) {
      this.pos.push(v.x, v.y, v.z);
      this.nor.push(this.n.x, this.n.y, this.n.z);
      this.colr.push(color.r, color.g, color.b);
    }
  }

  toGeometry(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nor, 3));
    g.setAttribute('color', new THREE.Float32BufferAttribute(this.colr, 3));
    g.computeBoundingSphere();
    return g;
  }
}

export function makeCanvas(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas unavailable');
  return { canvas, ctx };
}
